import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useVendor } from '../../contexts/VendorContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Store, Phone, Mail, MapPin, FileText, Building, User, 
  ArrowRight, ArrowLeft, CheckCircle, Loader2, ShieldCheck 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh"
];

export default function VendorRegisterPage() {
  const [step, setStep] = useState(1); // 1: Phone, 2: OTP, 3: Business Info, 4: KYC
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  const [formData, setFormData] = useState({
    business_name: '',
    owner_name: '',
    email: '',
    description: '',
    logo_url: '',
    gst_rate: 18,
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    kyc: {
      gst_number: '',
      pan_number: '',
      bank_account: '',
      bank_ifsc: '',
      bank_name: ''
    }
  });
  
  const [errors, setErrors] = useState({});
  const otpInputRefs = useRef([]);
  const navigate = useNavigate();
  const { isAuthenticated } = useVendor();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/vendor/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (step === 2 && otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, [step]);

  const handleSendOTP = async () => {
    if (phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    setLoading(true);
    try {
      const formattedPhone = '+91' + phone.replace(/\D/g, '');
      await axios.post(`${API_URL}/api/vendor/send-otp`, { phone: formattedPhone });
      toast.success('OTP sent to your phone');
      setStep(2);
      setResendTimer(30);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const code = otp.join('');
    if (code.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const formattedPhone = '+91' + phone.replace(/\D/g, '');
      // For registration, we just verify OTP is valid but don't login yet
      // We'll complete registration first
      setStep(3);
      toast.success('Phone verified! Continue with registration.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateBusinessInfo = () => {
    const newErrors = {};
    if (!formData.business_name.trim()) newErrors.business_name = 'Business name is required';
    if (!formData.owner_name.trim()) newErrors.owner_name = 'Owner name is required';
    if (!formData.address.street.trim()) newErrors['address.street'] = 'Address is required';
    if (!formData.address.city.trim()) newErrors['address.city'] = 'City is required';
    if (!formData.address.state) newErrors['address.state'] = 'State is required';
    if (!formData.address.pincode.trim()) newErrors['address.pincode'] = 'Pincode is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBusinessSubmit = () => {
    if (validateBusinessInfo()) {
      setStep(4);
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    try {
      const formattedPhone = '+91' + phone.replace(/\D/g, '');
      
      const registrationData = {
        phone: formattedPhone,
        business_name: formData.business_name,
        owner_name: formData.owner_name,
        email: formData.email || null,
        description: formData.description || null,
        logo_url: formData.logo_url || null,
        gst_rate: parseFloat(formData.gst_rate) || 18,
        address: formData.address,
        kyc: formData.kyc.gst_number || formData.kyc.pan_number ? formData.kyc : null
      };
      
      await axios.post(`${API_URL}/api/vendor/register`, registrationData);
      
      toast.success('Registration submitted! Pending admin approval.');
      navigate('/vendor/login', { state: { registered: true } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((s, idx) => (
        <React.Fragment key={s}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
            step >= s ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'
          }`}>
            {step > s ? <CheckCircle className="w-5 h-5" /> : s}
          </div>
          {idx < 3 && (
            <div className={`w-16 h-1 ${step > s ? 'bg-green-600' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="flex items-center justify-center space-x-3 mb-4">
            <Store className="w-10 h-10 text-green-700" />
            <span className="text-2xl font-bold text-green-800">nammahut vendor</span>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Become a Seller</h1>
          <p className="text-gray-600">Join India's marketplace for traditional products</p>
        </div>

        {renderStepIndicator()}

        <Card className="border-2 border-green-100 shadow-xl">
          {/* Step 1: Phone Number */}
          {step === 1 && (
            <>
              <CardHeader className="text-center bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <CardTitle>Verify Your Phone</CardTitle>
                <CardDescription>We'll send an OTP to verify your number</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex">
                    <div className="flex items-center px-4 bg-gray-100 border-2 border-r-0 border-green-200 rounded-l-lg">
                      <span className="text-gray-600 font-medium">+91</span>
                    </div>
                    <Input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="flex-1 border-2 border-green-200 rounded-l-none text-lg"
                      placeholder="Enter phone number"
                      maxLength={10}
                    />
                  </div>
                  <Button 
                    onClick={handleSendOTP} 
                    className="w-full btn-primary py-6"
                    disabled={loading || phone.length < 10}
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    Get OTP
                  </Button>
                </div>
                
                <p className="text-center text-sm text-gray-500 mt-6">
                  Already registered?{' '}
                  <Link to="/vendor/login" className="text-green-600 font-medium hover:underline">
                    Login here
                  </Link>
                </p>
              </CardContent>
            </>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <>
              <CardHeader className="text-center bg-gradient-to-r from-green-50 to-emerald-50">
                <button onClick={() => setStep(1)} className="absolute left-4 top-4 p-2 hover:bg-green-200 rounded-full">
                  <ArrowLeft className="w-5 h-5 text-green-700" />
                </button>
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <ShieldCheck className="w-8 h-8 text-white" />
                </div>
                <CardTitle>Enter OTP</CardTitle>
                <CardDescription>Sent to +91 {phone}</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex justify-center gap-2 mb-6">
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => (otpInputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 border-green-200"
                    />
                  ))}
                </div>
                
                <Button 
                  onClick={handleVerifyOTP}
                  className="w-full btn-primary py-6"
                  disabled={loading || otp.join('').length !== 6}
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                  Verify & Continue
                </Button>
                
                <p className="text-center text-sm text-gray-500 mt-4">
                  Didn't receive OTP?{' '}
                  {resendTimer > 0 ? (
                    <span>Resend in {resendTimer}s</span>
                  ) : (
                    <button onClick={handleSendOTP} className="text-green-600 font-medium hover:underline">
                      Resend OTP
                    </button>
                  )}
                </p>
              </CardContent>
            </>
          )}

          {/* Step 3: Business Information */}
          {step === 3 && (
            <>
              <CardHeader className="text-center bg-gradient-to-r from-green-50 to-emerald-50">
                <button onClick={() => setStep(2)} className="absolute left-4 top-4 p-2 hover:bg-green-200 rounded-full">
                  <ArrowLeft className="w-5 h-5 text-green-700" />
                </button>
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Building className="w-8 h-8 text-white" />
                </div>
                <CardTitle>Business Information</CardTitle>
                <CardDescription>Tell us about your business</CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Business Name *</label>
                    <Input
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      placeholder="Your Store Name"
                      className={errors.business_name ? 'border-red-500' : ''}
                    />
                    {errors.business_name && <p className="text-xs text-red-500 mt-1">{errors.business_name}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Owner Name *</label>
                    <Input
                      name="owner_name"
                      value={formData.owner_name}
                      onChange={handleInputChange}
                      placeholder="Your Full Name"
                      className={errors.owner_name ? 'border-red-500' : ''}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Email (Optional)</label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="business@example.com"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Business Description</label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Tell customers about your products and story..."
                    rows={3}
                  />
                </div>

                {/* Store Logo and GST Settings */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Store className="w-4 h-4" /> Store Settings
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Store Logo URL</label>
                      <Input
                        name="logo_url"
                        value={formData.logo_url}
                        onChange={handleInputChange}
                        placeholder="https://example.com/logo.png"
                      />
                      <p className="text-xs text-gray-500 mt-1">Enter URL of your store logo image</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Default GST Rate (%)</label>
                      <select
                        name="gst_rate"
                        value={formData.gst_rate}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded-lg bg-white"
                      >
                        <option value="0">0% (Exempt)</option>
                        <option value="5">5%</option>
                        <option value="12">12%</option>
                        <option value="18">18% (Default)</option>
                        <option value="28">28%</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Can be changed per product later</p>
                    </div>
                  </div>
                  
                  {formData.logo_url && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-2">Logo Preview:</p>
                      <img 
                        src={formData.logo_url} 
                        alt="Store logo preview" 
                        className="h-16 w-16 object-contain rounded-lg border bg-white"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Pickup Address
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Street Address *</label>
                      <Input
                        name="address.street"
                        value={formData.address.street}
                        onChange={handleInputChange}
                        placeholder="Building, Street, Area"
                        className={errors['address.street'] ? 'border-red-500' : ''}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">City *</label>
                        <Input
                          name="address.city"
                          value={formData.address.city}
                          onChange={handleInputChange}
                          placeholder="City"
                          className={errors['address.city'] ? 'border-red-500' : ''}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Pincode *</label>
                        <Input
                          name="address.pincode"
                          value={formData.address.pincode}
                          onChange={handleInputChange}
                          placeholder="600001"
                          maxLength={6}
                          className={errors['address.pincode'] ? 'border-red-500' : ''}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700">State *</label>
                      <Select
                        value={formData.address.state}
                        onValueChange={(value) => handleInputChange({ target: { name: 'address.state', value } })}
                      >
                        <SelectTrigger className={errors['address.state'] ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select State" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDIAN_STATES.map(state => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                <Button onClick={handleBusinessSubmit} className="w-full btn-primary py-6">
                  Continue to KYC
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </>
          )}

          {/* Step 4: KYC (Optional) */}
          {step === 4 && (
            <>
              <CardHeader className="text-center bg-gradient-to-r from-green-50 to-emerald-50">
                <button onClick={() => setStep(3)} className="absolute left-4 top-4 p-2 hover:bg-green-200 rounded-full">
                  <ArrowLeft className="w-5 h-5 text-green-700" />
                </button>
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <CardTitle>KYC Details</CardTitle>
                <CardDescription>Optional - Can be added later</CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                  <strong>Note:</strong> KYC is optional during registration. You can complete it later from your dashboard. Full KYC is required for payouts above ₹50,000.
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">GST Number</label>
                    <Input
                      name="kyc.gst_number"
                      value={formData.kyc.gst_number}
                      onChange={handleInputChange}
                      placeholder="22AAAAA0000A1Z5"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">PAN Number</label>
                    <Input
                      name="kyc.pan_number"
                      value={formData.kyc.pan_number}
                      onChange={handleInputChange}
                      placeholder="ABCDE1234F"
                    />
                  </div>
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Bank Details for Payouts</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Bank Name</label>
                      <Input
                        name="kyc.bank_name"
                        value={formData.kyc.bank_name}
                        onChange={handleInputChange}
                        placeholder="State Bank of India"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Account Number</label>
                        <Input
                          name="kyc.bank_account"
                          value={formData.kyc.bank_account}
                          onChange={handleInputChange}
                          placeholder="1234567890"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">IFSC Code</label>
                        <Input
                          name="kyc.bank_ifsc"
                          value={formData.kyc.bank_ifsc}
                          onChange={handleInputChange}
                          placeholder="SBIN0001234"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={handleFinalSubmit} 
                    className="flex-1 py-6"
                    disabled={loading}
                  >
                    Skip for Now
                  </Button>
                  <Button 
                    onClick={handleFinalSubmit} 
                    className="flex-1 btn-primary py-6"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    Complete Registration
                  </Button>
                </div>
              </CardContent>
            </>
          )}
        </Card>

        {/* Benefits Section */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div className="p-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Store className="w-6 h-6 text-green-600" />
            </div>
            <h4 className="font-medium text-gray-900">Zero Setup Cost</h4>
            <p className="text-sm text-gray-600">Start selling without any upfront fees</p>
          </div>
          <div className="p-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Building className="w-6 h-6 text-green-600" />
            </div>
            <h4 className="font-medium text-gray-900">Pan-India Reach</h4>
            <p className="text-sm text-gray-600">Ship to customers across India</p>
          </div>
          <div className="p-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <FileText className="w-6 h-6 text-green-600" />
            </div>
            <h4 className="font-medium text-gray-900">Easy Payouts</h4>
            <p className="text-sm text-gray-600">Weekly settlements to your bank</p>
          </div>
        </div>
      </div>
    </div>
  );
}
